/**
 * 训练模型删除确认弹窗
 * v10.2 新增：需要输入「删除」进行二次确认
 */

import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import { AlertTriangle, Loader2 } from 'lucide-react'
import type { VoiceTrainingModel } from '@/modules/identity/types'

interface VoiceModelDeleteDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  model: VoiceTrainingModel | null
  onConfirm: () => Promise<void>
  isDeleting?: boolean
}

export function VoiceModelDeleteDialog({
  open,
  onOpenChange,
  model,
  onConfirm,
  isDeleting = false,
}: VoiceModelDeleteDialogProps) {
  const [confirmText, setConfirmText] = useState('')

  // 弹窗打开时重置输入
  useEffect(() => {
    if (open) {
      setConfirmText('')
    }
  }, [open])

  const canConfirm = confirmText === '删除'

  const handleConfirm = async () => {
    if (!canConfirm) return
    await onConfirm()
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <AlertTriangle className="h-5 w-5" />
            确认删除训练模型
          </DialogTitle>
          <DialogDescription>
            此操作不可撤销，请仔细确认。
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {model && (
            <div className="p-3 bg-slate-50 rounded-lg space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-500">模型名称</span>
                <span className="text-sm font-medium">{model.name}</span>
              </div>
              <div className="flex items-start justify-between">
                <span className="text-sm text-slate-500">模型 ID</span>
                <span
                  className="text-xs font-mono text-slate-600 text-right max-w-[200px] break-all"
                >
                  {model.model_id}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-500">训练时间</span>
                <span className="text-sm">
                  {new Date(model.trained_at).toLocaleString('zh-CN')}
                </span>
              </div>
            </div>
          )}

          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-700 flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <span>
                此操作将<strong>同时删除 CosyVoice 平台上的克隆音色</strong>，不可恢复！
              </span>
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirm-delete">
              请输入「<span className="text-red-600 font-medium">删除</span>」确认：
            </Label>
            <Input
              id="confirm-delete"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="删除"
              autoComplete="off"
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isDeleting}
          >
            取消
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={!canConfirm || isDeleting}
          >
            {isDeleting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                删除中...
              </>
            ) : (
              '确认删除'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
